/**
 * Iter51 Etapa 15 — AgentCombobox
 *
 * Searchable dropdown for agent reassignment on Sale.
 * Sourced from /api/agents (active by default). Shows agency + linked user info.
 * Returns the Agent.id (commercial_agent_id) on selection.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Check, ChevronsUpDown, User as UserIcon, Search, Mail } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "./ui/command";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AgentCombobox = ({
  value,                         // currently selected Agent.id (commercial_agent_id)
  currentLabel,                  // displayed label when value is set (e.g. agent_name from sale)
  onChange,                      // (agentId, agentObj) => void
  placeholder = "Alege agent...",
  testId = "agent-combobox",
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { limit: 100, active: true };
        if (query.trim()) params.search = query.trim();
        const r = await axios.get(`${API_URL}/agents`, { params });
        if (!cancel) setItems(r.data?.data || []);
      } catch {
        if (!cancel) setItems([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    }, 200);
    return () => { cancel = true; clearTimeout(t); };
  }, [query, open]);

  const selectedLabel = useMemo(() => {
    if (!value) return "";
    // Try lookup in current items, fall back to currentLabel passed by parent
    const inList = items.find((a) => a.id === value);
    return inList?.full_name || currentLabel || "";
  }, [value, items, currentLabel]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between font-normal text-sm h-8 ${className}`}
          data-testid={testId}
        >
          <span className="truncate flex items-center gap-1.5 min-w-0">
            {selectedLabel ? (
              <>
                <UserIcon className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                <span className="truncate">{selectedLabel}</span>
              </>
            ) : (
              <span className="text-slate-400">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Caută agent după nume sau marcă..."
            value={query}
            onValueChange={setQuery}
            data-testid={`${testId}-input`}
          />
          <CommandList>
            {loading && (
              <div className="px-3 py-4 text-xs text-slate-400 flex items-center gap-2">
                <Search className="h-3 w-3 animate-pulse" /> Caut...
              </div>
            )}
            {!loading && items.length === 0 && (
              <CommandEmpty>
                <div className="px-3 py-4 text-center text-xs text-slate-500">
                  Niciun agent găsit{query.trim() ? ` pentru „${query.trim()}"` : ""}.
                </div>
              </CommandEmpty>
            )}
            {!loading && items.length > 0 && (
              <CommandGroup heading={`${items.length} agent${items.length === 1 ? "" : "i"}`}>
                {items.map((a) => {
                  const selected = value === a.id;
                  return (
                    <CommandItem
                      key={a.id}
                      value={a.id}
                      onSelect={() => { onChange(a.id, a); setOpen(false); }}
                      data-testid={`${testId}-option-${a.id}`}
                      className="flex items-start gap-2"
                    >
                      <Check className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${selected ? "opacity-100 text-blue-600" : "opacity-0"}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{a.full_name || `${a.first_name || ""} ${a.last_name || ""}`.trim()}</p>
                        <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
                          {a.primary_marca_code && <span className="font-mono">{a.primary_marca_code}</span>}
                          {a.agency_name && (
                            <>
                              <span className="text-slate-300">·</span>
                              <span className="truncate">{a.agency_name}</span>
                            </>
                          )}
                        </div>
                        {a.linked_user_email && (
                          <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1 truncate">
                            <Mail className="h-2.5 w-2.5" />
                            {a.linked_user_email}
                          </p>
                        )}
                      </div>
                      {!a.linked_user_id && (
                        <span className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded flex-shrink-0">
                          fără user
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default AgentCombobox;
